export interface LabelAndDescription {
  label: string;
  description?: string;
}

export interface NameAndWidth {
  name: string;
  width: string;
}

export interface LabelAndSelected {
  label: string;
  selected?: boolean;
}
export interface PatientWithNextInterview {
  id: string;
  first_name: string;
  last_name: string;
  date: string;
  start_time?: string;
  is_active: boolean;
  date_added: Date;
  mobile: string;
  email: string;
}

export interface PatientEssentialInfo {
  id: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
}

export interface Reminders {
  text: string;
  completed: boolean;
}

export interface Interview {
  date: string;
  title: string;
}

export interface InterviewNotes {
  categoria: string;
  valori: string[];
  descrizione: string;
}

export interface MedicalHistoryField {
  label: string;
  description: string;
}

export interface FamilyHistoryField {
  relative: string;
  name: string;
  occupation: string;
  age: string;
  other: string;
}

export interface DiagnosisTableRow {
  codex: string;
  description: string;
  date: string;
  note: string;
}

export interface PharmacoterapyTableRow {
  activePrinciple: string[];
  description: string;
}

export interface Document {
  category: string;
  title: string;
}

export interface SignificantEvent {
  date: string;
  title: string;
}

export interface PatientNote {
  id: string;
  patientId: string;
  dateLastModified: string;
  description: string;
}

export interface PatientReminder {
  id: string;
  patientId: string;
  description: string;
  completed: boolean;
  dateLastModified: string;
}

export interface PatientAppointment {
  id: string;
  patient_id: string;
  date: string;
  start_time: string;
  end_time?: string;
  title: string;
  is_remote?: boolean;
  google_calendar_event_id?: string;
  appointment_notes?: string;
  ai_pipeline_state?: string;
  transcription_state?: string;
  appsumm_state?: string;
  appwordcloud_state?: string;
  apptopics_state?: string;
  catdistr_state?: string;
}

export interface PatientNames {
  id: string;
  first_name: string;
  last_name: string;
}
export interface CalendarPatient {
  id: string;
  first_name: string;
  last_name: string;
  count?: number;
}
export interface Patient {
  [key: string]: string | boolean;
  id: string;
  is_natural_person: boolean;
  is_active: boolean;
  first_name: string;
  last_name: string;
  gender: string;
  date_of_birth: string;
  place_of_birth: string;
  province_of_birth: string;
  country_of_birth: string;
  tax_code: string;
  email: string;
  mobile: string;
  company_name: string;
  vat_number: string;
  unique_code: string;
  certified_email: string;
  residence_address: string;
  residence_street_number: string;
  residence_city: string;
  residence_province: string;
  residence_zip_code: string;
  fiscal_address: string;
  fiscal_street_number: string;
  fiscal_city: string;
  fiscal_province: string;
  fiscal_zip_code: string;
  client_is_payer: boolean;
}

export interface RegistryPatient {
  first_name: string;
  last_name: string;
  mobile: string;
  email: string;
  gender: string;
  date_of_birth: string | Date;
  place_of_birth: string;
  province_of_birth: string;
  country_of_birth: string;
  tax_code: string;
}

export interface ActivePatientFieldsForAiAnalysis {
  id: string;
  first_name: string;
  last_name: string;
  patsumm_state: string;
  patdmr_state: string;
  patient_overview_pipeline_state: string;
  num_speakers: number;
  audio_language: string;
}

export interface TodayAppointments {
  id: string;
  patient_id: string;
  patient_first_name: string;
  patient_last_name: string;
  date: string;
  start_time: string;
  is_remote: boolean;
}

export const patientVisibleList: string[] = [
  'first_name',
  'last_name',
  'gender',
  'date_of_birth',
  'place_of_birth',
  'province_of_birth',
  'country_of_birth',
  'tax_code',
  'email',
  'mobile',
  'client_is_payer',
];
export const patientPersonList: string[] = [
  'residence_address',
  'residence_street_number',
  'residence_city',
  'residence_province',
  'residence_zip_code',
];
export const patientVatList: string[] = [
  'company_name',
  'vat_number',
  'unique_code',
  'certified_email',
  'fiscal_address',
  'fiscal_street_number',
  'fiscal_city',
  'fiscal_province',
  'fiscal_zip_code',
];

export interface PatientField {
  label: string;
  description: string | boolean;
  class?: string;
}

export interface PatientDiagnoses {
  id: string;
  patient_id: string;
  code: string;
  description: string;
  date: string;
  notes: string;
}

export interface PatientPharmacotherapy {
  id: string;
  patient_id: string;
  active_principle: string;
  description: string;
}

export interface LifeLineEvent {
  id: string;
  patient_id: string;
  date: string;
  title: string;
}

export interface PatientAnamnesis {
  [key: string]: string | number;
  id: string;
  patient_id: string;
  occupation: string;
  hospitalizations_and_surgical_operations: string;
  marital_status: string;
  pharmacological_anamnesis: string;
  number_of_children: string;
  pathologies: string;
  sexual_orientation: string;
  substances: string;
  hobbies: string;
  psychomotor_development: string;
  age: string;
  sports: string;
  allergies: string;
  years_of_education: string;
  supports: string;
  previous_clinical_paths: string;
}

export const anamnesisPersonalFields: string[] = [
  'age',
  'years_of_education',
  'occupation',
  'marital_status',
  'number_of_children',
  'sexual_orientation',
  'hobbies',
  'sports',
  'supports',
];

export const anamnesisClinicHistoryFields: string[] = [
  'previous_clinical_paths',
  'hospitalizations_and_surgical_operations',
  'pharmacological_anamnesis',
  'pathologies',
  'substances',
];

export interface PatientFamilyMember {
  id: string;
  patient_id: string;
  relationship: string;
  name: string;
  age: number;
  occupation: string;
  other: string;
}

export const payerNaturalPersonFields: string[] = [
  'first_name',
  'last_name',
  'gender',
  'date_of_birth',
  'country_of_birth',
  'place_of_birth',
  'province_of_birth',
  'tax_code',
  'residence_address',
  'residence_street_number',
  'residence_city',
  'residence_province',
  'residence_zip_code',
];

export const payerVatPersonFields: string[] = [
  'company_name',
  'certified_email',
  'vat_number',
  'unique_code',
  'fiscal_address',
  'fiscal_street_number',
  'fiscal_city',
  'fiscal_province',
  'fiscal_zip_code',
];

export interface PatientDocument {
  id: string;
  patient_id: string;
  title: string;
  file_content_type: string;
  date: string;
}
